import { useState } from "react";
import { useFormik } from "formik";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  FinanceCategoryRequestDTO,
  useCreateFinanceCategoryMutation,
  useToast,
} from "@/hooks";
import { FinanceCategoryValidationSchema } from "@/shared";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { DisplayError } from "@/utils";
import { ImportFinanceCategoryModal } from "./import-finance-category-modal";

type FormCreateFinanceCategoryProps = {
  handleClose: () => void;
};

export function FormCreateFinanceCategory({
  handleClose,
}: FormCreateFinanceCategoryProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const initialValues = {
    name: "",
  };

  const { mutateAsync, isLoading } = useCreateFinanceCategoryMutation();
  const { handleSubmit, getFieldProps, errors, touched, resetForm } = useFormik(
    {
      initialValues,
      validationSchema: FinanceCategoryValidationSchema(),
      onSubmit: async (categoryData) => {
        handleCreateCategorySubmit(categoryData);
      },
    }
  );

  async function handleCreateCategorySubmit(
    categoryData: FinanceCategoryRequestDTO
  ) {
    try {
      mutateAsync(categoryData, {
        onSuccess: () => {
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.created successfully"),
          });
          resetForm();
          handleClose();
        },
        onError: (error) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message")),
            variant: "destructive",
          });
        },
      });
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-10 px-4 pb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-zinc-700">Nova Categoria Financeira</h2>
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsImportModalOpen(true)}
          className="text-sm"
        >
          Importar CSV
        </Button>
      </div>
      <div className="flex flex-col gap-2">
        <Label>{t("LABEL.name")}</Label>
        <div className="space-y-1">
          <Input
            isInvalid={touched.name && errors.name}
            isValid={touched.name && !errors.name}
            {...getFieldProps("name")}
          />
          {touched.name && errors.name ? (
            <span className="text-red-500 text-sm min-h-3">{errors.name}</span>
          ) : (
            <div className="min-h-3"></div>
          )}
        </div>
      </div>
      <Button isLoading={isLoading} className="w-full">
        {t("LABEL.save")}
      </Button>
      
      <ImportFinanceCategoryModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={handleClose}
      />
    </form>
  );
}
